<template>
  <Text variant="label-small" class="user-pic" :class="userColor">
    {{ userInitials }}
    <div v-if="iconName" :class="['icon-status', status]">
      <Icon :name="iconName" class="icon" />
    </div>
  </Text>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import { IconName } from '../../values/icons.const';
import Icon from '../icon.vue';
import Text from '../text.vue';
import { AvatarProps } from './avatar-props';
import { AvatarStatus } from './avatar-status';

const props = withDefaults(defineProps<AvatarProps>(), {
  showSingleLetter: false,
});

const userInitials = computed(() => {
  let names = props.userName.toUpperCase().split(' ');

  if (props.showSingleLetter) {
    return names[0].charAt(0);
  }

  return names.map((name) => name.charAt(0)).join('');
});

const colors = ['blue', 'green', 'violet', 'orange', 'yellow'];

const iconsMap = new Map<AvatarStatus, IconName>([
  ['accepted', 'check'],
  ['pending', 'question-mark'],
  ['declined', 'x-close'],
]);

const iconName = computed(() => iconsMap.get(props.status!));

// Assign a color based on the first letter of the username
const userColor = computed(() => {
  let index = props.userName.charCodeAt(0) % 4;

  return colors[index];
});
</script>

<style lang="scss" scoped>
.user-pic {
  --avatar-size: 2.25rem;

  color: var(--t-color-neutral-900);
  min-width: var(--avatar-size);
  width: var(--avatar-size);
  height: var(--avatar-size);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--t-border-radius-full-rounded);
  position: relative;

  &.blue {
    background-color: var(--t-color-blue-200);
  }

  &.green {
    background-color: var(--t-color-green-200);
  }

  &.violet {
    background-color: var(--t-color-violet-200);
  }

  &.orange {
    background-color: var(--t-color-orange-200);
  }

  &.yellow {
    background-color: var(--t-color-yellow-200);
  }

  .icon-status {
    --icon-status-size: 14px;
    --icon-status-border-size: 1px;

    position: absolute;
    bottom: -1px;
    right: -1px;

    width: var(--icon-status-size);
    height: var(--icon-status-size);

    display: flex;
    justify-content: center;
    align-items: center;

    border: var(--icon-status-border-size) solid #fff;
    border-radius: var(--t-border-radius-half-rounded);
    color: var(--t-color-neutral-white);

    &.accepted {
      background-color: var(--t-color-green-500);
    }

    &.pending {
      background-color: var(--t-color-neutral-500);
    }

    &.declined {
      background-color: var(--t-color-red-400);
    }

    .icon {
      --t-icon-size: var(--t-spacing-sm) !important;
    }
  }
}
</style>
